import React from 'react';
import {
  MdKeyboardArrowDown,
  MdNavigateBefore,
  MdNavigateNext,
  MdRefresh,
} from 'react-icons/md';

interface PaginationProps {
  pageNumber: number;
  totalPages: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onReload?: () => void;
  showPageSize?: boolean;
  showReload?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  pageNumber,
  totalPages,
  pageSize,
  hasNext,
  hasPrevious,
  loading = false,
  onPageChange,
  onPageSizeChange,
  onReload,
  showPageSize = true,
  showReload = true,
}) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          {showPageSize && (
            <>
              <label className="text-sm font-medium text-gray-700">
                Page Size:
              </label>
              <div className="relative">
                <select
                  value={pageSize}
                  onChange={e => onPageSizeChange(Number(e.target.value))}
                  className="appearance-none border border-gray-300 rounded-md pl-3 pr-9 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer hover:border-gray-400 transition-colors"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <MdKeyboardArrowDown className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            </>
          )}

          {showReload && onReload && (
            <button
              onClick={onReload}
              disabled={loading}
              className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <MdRefresh
                size={16}
                className={`${loading ? 'animate-spin' : ''}`}
              />
              <span>Reload</span>
            </button>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center space-x-1">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
            <div
              className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
              style={{ animationDelay: '0.1s' }}
            ></div>
            <div
              className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
              style={{ animationDelay: '0.2s' }}
            ></div>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(pageNumber - 1)}
            disabled={!hasPrevious || loading}
            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <MdNavigateBefore size={20} />
          </button>

          <span className="text-sm text-gray-700">
            Page {pageNumber} of {totalPages}
          </span>

          <button
            onClick={() => onPageChange(pageNumber + 1)}
            disabled={!hasNext || loading}
            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <MdNavigateNext size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};
